import { EyeOutlined } from '@ant-design/icons';
import type { UserWithStats } from '@shop-monorepo/types';
import type { TableProps } from 'antd';
import { Button, Tag } from 'antd';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { BottomPagination } from '../../components/BottomPagination';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  StyledTable,
  TableContainer,
  Toolbar,
} from '../../components/PageLayout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useListNavigate } from '../../hooks/useListReturn';
import { usePagination } from '../../hooks/usePagination';
import { useTableSorter } from '../../hooks/useTableSorter';
import { useBooleanParams, useStringParams } from '../../hooks/useUrlParam';
import { useUsers } from '../../hooks/useUserQueries';
import { ROUTES } from '../../routes/routes';

import { UserFilters } from './UserFilters';

const UserCard = styled.div`
  background: #f8fafc;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UserCardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const UserCardMeta = styled.div`
  font-size: 13px;
  color: #64748b;
`;

export const UsersPage: React.FC = () => {
  const goToDetail = useListNavigate();
  const { page, pageSize } = usePagination();
  const { sorter, handleTableChange } = useTableSorter();
  const isMobile = useIsMobile();

  const booleanFilters = useBooleanParams(['marketingOptIn', 'hasPassword']);
  const stringFilters = useStringParams(['role']);

  const filters = useMemo(
    () => ({ ...booleanFilters, ...stringFilters }),
    [booleanFilters, stringFilters],
  );

  const { data: paginatedUsers, isLoading } = useUsers({ page, limit: pageSize, filters, sorter });
  const users = paginatedUsers?.hits || [];
  const totalUsers = paginatedUsers?.total || 0;

  const columns: TableProps<UserWithStats>['columns'] = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: "Ім'я та прізвище",
      key: 'fullName',
      render: (_, record) =>
        record.firstName || record.lastName
          ? [record.firstName, record.lastName].filter(Boolean).join(' ')
          : '—',
    },
    {
      title: 'Телефон',
      key: 'phone',
      render: (_, record) => record.phone || '—',
    },
    {
      title: 'Роль',
      key: 'role',
      render: (_, record) =>
        record.role === 'admin' ? <Tag color="red">Адмін</Tag> : <Tag color="blue">Користувач</Tag>,
    },
    {
      title: 'Вхід',
      key: 'authMethod',
      render: (_, record) =>
        record.hasPassword ? <Tag>Пароль</Tag> : <Tag color="orange">Google</Tag>,
    },
    {
      title: 'Розсилка',
      key: 'marketingOptIn',
      render: (_, record) => (record.marketingOptIn ? <Tag color="green">Так</Tag> : <Tag>Ні</Tag>),
    },
    {
      title: 'Замовлень',
      dataIndex: 'orderCount',
      key: 'orderCount',
      sorter: true,
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => goToDetail(ROUTES.USER_VIEW.replace(':id', String(record.id)))}
        />
      ),
    },
    {
      title: 'Реєстрації',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date: string) =>
        new Date(date).toLocaleDateString('uk-UA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Користувачі</PageTitle>
      </PageHeader>
      <Toolbar>
        <UserFilters />
      </Toolbar>
      {isMobile ? (
        <>
          {isLoading
            ? null
            : users.map((user) => {
                const fullName =
                  user.firstName || user.lastName
                    ? [user.firstName, user.lastName].filter(Boolean).join(' ')
                    : '—';
                return (
                  <UserCard key={user.id}>
                    <UserCardRow>
                      <span style={{ fontWeight: 700 }}>{fullName}</span>
                      {user.role === 'admin' ? (
                        <Tag color="red">Адмін</Tag>
                      ) : (
                        <Tag color="blue">Користувач</Tag>
                      )}
                    </UserCardRow>
                    <UserCardMeta>{user.email}</UserCardMeta>
                    <UserCardRow>
                      <UserCardMeta>
                        {user.phone || '—'} · {user.orderCount} замовлень
                      </UserCardMeta>
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => goToDetail(ROUTES.USER_VIEW.replace(':id', String(user.id)))}
                      />
                    </UserCardRow>
                  </UserCard>
                );
              })}
        </>
      ) : (
        <TableContainer>
          <StyledTable
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </TableContainer>
      )}
      <BottomPagination total={totalUsers} />
    </PageContainer>
  );
};
